import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { usePharmacyWarnings } from '@/lib/api/adminHooks';
import type { PharmacyWarningInclude, PharmacyWarningRow } from '@/types/admin';

import { PharmacyWarningCard } from './PharmacyWarningCard';

/**
 * Dedicated admin warnings page — unresolved/resolved/all tabs, full
 * action UI (when unresolved), resolution metadata (when resolved).
 *
 * Reachable from the summary card on /admin/clinical-ops.
 */
export function PharmacyWarningsScreen() {
  const [tab, setTab] = useState<PharmacyWarningInclude>('unresolved');
  const [query, setQuery] = useState('');
  const { data, isLoading, isError, error } = usePharmacyWarnings(tab);

  const warnings = data?.warnings ?? [];
  const filtered = useMemo(
    () => filterWarnings(warnings, query),
    [warnings, query],
  );

  return (
    <div className="flex flex-col gap-24">
      <Header data={data} />

      <Tabs value={tab} onChange={setTab} data={data} />

      <SearchBar value={query} onChange={setQuery} />

      {isLoading ? (
        <p className="type-body-sm text-ink-muted">Loading…</p>
      ) : isError ? (
        <p className="type-body-sm text-status-danger">
          Failed to load: {String((error as Error)?.message || error)}
        </p>
      ) : warnings.length === 0 ? (
        <EmptyState tab={tab} />
      ) : filtered.length === 0 ? (
        <p className="type-body-sm text-ink-muted">
          No warnings match "{query}".
        </p>
      ) : (
        <ListBody warnings={filtered} />
      )}
    </div>
  );
}

function SearchBar({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-8">
      <input
        type="text"
        placeholder="Search by patient name, email, order number, or warning code…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-default rounded-sm px-12 py-8 type-body-sm bg-surface-card text-ink w-full max-w-content-md"
      />
      {value ? (
        <button
          type="button"
          className="btn-ghost btn-sm"
          onClick={() => onChange('')}
        >
          Clear
        </button>
      ) : null}
    </div>
  );
}

function filterWarnings(
  warnings: PharmacyWarningRow[],
  query: string,
): PharmacyWarningRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return warnings;
  return warnings.filter((w) => {
    const haystack = [
      w.patient_first_name,
      w.patient_last_name,
      [w.patient_first_name, w.patient_last_name].filter(Boolean).join(' '),
      w.patient_email,
      w.order_number,
      w.partner_order_id,
      String(w.reason_code ?? ''),
      w.reason_description,
      w.product_label,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}

function Header({
  data,
}: {
  data: { unresolved_count: number; resolved_count: number } | undefined;
}) {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-baseline gap-12">
        <Link
          to="/admin/clinical-ops"
          className="type-body-sm text-accent hover:underline"
        >
          ← Clinical ops
        </Link>
      </div>
      <h1 className="type-heading-xl">Pharmacy warnings</h1>
      <p className="type-body-md text-ink-muted max-w-content-sm">
        Partner-flagged orders awaiting human review (PPI today). Each
        warning shows the supplemental notes verbatim alongside order,
        patient, and current shipping context. Submit any of the four
        actions to fire the corresponding partner update; dismiss for
        informational warnings.
        {data ? (
          <>
            {' '}
            <span className="text-ink">
              {data.unresolved_count} unresolved
            </span>{' '}
            · {data.resolved_count} resolved.
          </>
        ) : null}
      </p>
    </div>
  );
}

function Tabs({
  value,
  onChange,
  data,
}: {
  value: PharmacyWarningInclude;
  onChange: (v: PharmacyWarningInclude) => void;
  data: { unresolved_count: number; resolved_count: number } | undefined;
}) {
  const opts: { id: PharmacyWarningInclude; label: string; count?: number }[] = [
    { id: 'unresolved', label: 'Unresolved', count: data?.unresolved_count },
    { id: 'resolved', label: 'Resolved', count: data?.resolved_count },
    { id: 'all', label: 'All' },
  ];
  return (
    <div className="flex items-center gap-4 border-b border-subtle">
      {opts.map((o) => {
        const active = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className={
              active
                ? 'type-body-sm text-ink px-12 py-8 border-b-2 border-accent font-medium -mb-1px'
                : 'type-body-sm text-ink-muted hover:text-ink px-12 py-8 border-b-2 border-transparent -mb-1px'
            }
          >
            {o.label}
            {o.count != null ? (
              <span className="type-body-xs text-ink-muted ml-6">
                · {o.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function EmptyState({ tab }: { tab: PharmacyWarningInclude }) {
  const message =
    tab === 'unresolved'
      ? 'No unresolved pharmacy warnings — all clear.'
      : tab === 'resolved'
        ? 'No resolved pharmacy warnings yet.'
        : 'No pharmacy warnings on record.';
  return (
    <div className=" border border-dashed border-subtle p-24 text-center">
      <p className="type-body-sm text-ink-muted">{message}</p>
    </div>
  );
}

function ListBody({ warnings }: { warnings: PharmacyWarningRow[] }) {
  return (
    <div className="flex flex-col gap-12">
      {warnings.map((w) => (
        <PharmacyWarningCard
          key={`${w.order_id}:${w.reason_code}:${w.received_at}:${w.resolved_at ?? 'open'}`}
          w={w}
        />
      ))}
    </div>
  );
}
